import React, { useState } from 'react';
import { Box, Layers, Cuboid, Zap, CheckCircle2, Loader2, Download, AlertCircle, X, Calculator, PlusCircle } from 'lucide-react';
import { callGroqText, getGroqKey } from '../../services/groqService';
import { quotesApi } from '../../core/adapter-api';
import { v4 as uuidv4 } from 'uuid';

interface CadGeneratorProps {
  onClose: () => void;
  contextPrompt?: string; 
  inline?: boolean;
}

export const CadGenerator: React.FC<CadGeneratorProps> = ({ onClose, contextPrompt, inline }) => {
  const [prompt, setPrompt] = useState(contextPrompt || '');
  const [status, setStatus] = useState<'idle' | 'generating' | 'done' | 'error'>('idle');
  const [resultQuote, setResultQuote] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setStatus('generating');
    
    const key = getGroqKey();
    if (key) {
      try {
         const sysPrompt = "Eres un aparejador experto. A partir del DISEÑO o prompt del usuario, debes escupir un presupuesto realista y desglosado con precios de mercado en España (Euros). Devuelve ÚNICAMENTE la tabla en formato markdown y un total final. No hagas introducciones, ve directo al grano.";
         const res = await callGroqText(sysPrompt, prompt, 1500);
         setResultQuote(res);
         setStatus('done');
      } catch (e) {
         setStatus('error');
      }
    } else {
      // Simulación offline si no hay API vinculada
      setTimeout(() => {
        setResultQuote("### Presupuesto IA (Simulación Local)\n\n| Partida | Descripción | Importe Estimado |\n|---|---|---|\n| **Demolición** | Retirada tabiquería y desescombro | 1.850,00 € |\n| **Albañilería** | Refuerzos, pladur y aislamientos | 4.200,00 € |\n| **Instalaciones** | Suelo radiante aerotérmico y fontanería | 8.900,00 € |\n| **Acabados** | Pintura y revestimientos | 3.100,00 € |\n\n**TOTAL EJECUCIÓN MATERIAL: 18.050,00 €**\n\n*(Nota: Configura tu API Key de IA en Configuración para obtener algoritmos reales).*");
        setStatus('done');
      }, 3500);
    }
  };

  const handleCreateOfficial = async () => {
    if (!resultQuote) return;
    
    // Simple markdown parser
    const lines = resultQuote.split('\n');
    const quoteLines: any[] = [];
    let baseTotal = 0;
    
    for(const line of lines) {
        if(line.startsWith('|') && !line.includes('---|---|') && !line.includes('Partida |')) {
            const parts = line.split('|').map(s => s.trim()).filter(Boolean);
            if(parts.length >= 3) {
                const title = parts[0].replace(/\*/g, '');
                const desc = parts[1];
                let priceStr = parts[2].replace(/[^\d,.-]/g, '').replace('.', '').replace(',', '.');
                let price = parseFloat(priceStr) || 0;
                
                quoteLines.push({
                   id: uuidv4(),
                   description: `${title} - ${desc}`,
                   quantity: 1,
                   price: price, // Now using price directly, as defined in our ts fix earlier
                   unit: 'ud',
                   vatPct: 21,
                   discountPct: 0,
                   retentionPct: 0
                });
                baseTotal += price;
            }
        }
    }
    
    if(quoteLines.length === 0) {
        quoteLines.push({
           id: uuidv4(),
           description: 'Presupuesto Integral de Memoria Técnica (ver notas)',
           quantity: 1,
           price: baseTotal || 0,
           unit: 'ud',
           vatPct: 21,
           discountPct: 0,
           retentionPct: 0
        });
    }

    try {
      const now = Date.now();
      const newQuote = {
         id: uuidv4(),
         number: `PE-IA-${Math.floor(Math.random() * 10000)}`,
         date: new Date().toISOString().split('T')[0],
         status: 'draft',
         contactId: '', // Client needs to be assigned later
         notes: 'Estimación Generada por IA a partir de modelo conceptual:\n\n' + resultQuote,
         baseTotal: baseTotal,
         vatTotal: baseTotal * 0.21,
         reTotal: 0,
         retentionTotal: 0,
         grandTotal: baseTotal * 1.21,
         lines: quoteLines,
         templateId: 'default',
         createdAt: now,
         updatedAt: now
      };
      await quotesApi.add(newQuote as any);
      alert('¡Presupuesto Oficial creado en la base de datos!\nDirígete a Ventas > Presupuestos para revisar y asignar al cliente final.');
      if(onClose) onClose();
    } catch(e) {
      console.error(e);
      alert('Error guardando el presupuesto. Inténtalo de nuevo.');
    }
  };

  return (
    <div className={inline ? "w-full flex flex-col md:flex-row" : "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"} style={inline ? { height: '100%', minHeight: '500px' } : undefined}>
      <div className={`w-full bg-gray-950 border border-emerald-500/30 shadow-2xl flex flex-col md:flex-row shadow-emerald-500/10 overflow-hidden ${inline ? 'flex-1 border-0' : 'max-w-4xl rounded-2xl'}`}>
        
        {/* Panel Izquierdo: Configuración MCP */}
        <div className="w-full md:w-1/3 p-6 flex flex-col border-b md:border-b-0 md:border-r border-white/10 bg-white/5">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Cuboid className="text-emerald-400" />
              AutoCAD AI
            </h3>
            <button onClick={onClose} className="md:hidden text-white/40 hover:text-white"><X size={20} /></button>
          </div>
          
          <div className="flex-1 space-y-4">
            <div className="p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20 text-xs text-emerald-300">
              <span className="font-bold flex items-center gap-2 mb-1"><Zap size={14} /> gNucleus MCP Activo</span>
              El servidor MCP transformará tu solicitud en un mallado 3D STL/GLTF.
            </div>

            <div>
              <label className="text-xs font-mono text-white/50 mb-2 block">PROMPT DEL MODELO 3D</label>
              <textarea 
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ej: Cerramiento exterior de vallas metálicas negras con diseño minimalista de 2 metros de altura..."
                className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-sm text-white focus:border-emerald-500/50 outline-none resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
               <div className="p-2 border border-white/10 bg-white/5 rounded-lg text-center cursor-pointer hover:border-emerald-500/50">
                 <p className="text-xs text-white/60">Precisión</p>
                 <p className="text-sm font-bold text-emerald-400">Milimétrica</p>
               </div>
               <div className="p-2 border border-white/10 bg-white/5 rounded-lg text-center cursor-pointer hover:border-emerald-500/50">
                 <p className="text-xs text-white/60">Formato</p>
                 <p className="text-sm font-bold text-emerald-400">.GLTF</p>
               </div>
            </div>
          </div>

          <button 
            onClick={handleGenerate}
            disabled={status === 'generating' || !prompt.trim()}
            className="mt-6 w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:scale-[1.02]"
          >
            {status === 'generating' ? <Loader2 className="animate-spin" size={18} /> : <Calculator size={18} />}
            {status === 'generating' ? 'Calculando Partidas...' : 'Generar Presupuesto Base'}
          </button>
        </div>

        {/* Panel Derecho: Visualizador */}
        <div className={`w-full md:w-2/3 relative bg-black flex flex-col ${inline ? 'flex-1 min-h-[300px]' : 'h-80 md:h-[500px]'}`}>
          <button onClick={onClose} className="absolute top-4 right-4 z-10 text-white/40 hover:text-white hidden md:block"><X size={20} /></button>

          {/* Grid de fondo decorativo */}
          <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

          <div className="flex-1 flex items-center justify-center relative">
            {status === 'idle' && (
              <div className="text-center">
                <Layers size={48} className="text-white/10 mx-auto mb-4" />
                <p className="text-white/30 text-sm font-mono">Esperando prompt de entrada...</p>
              </div>
            )}

            {status === 'generating' && (
              <div className="text-center w-full max-w-sm px-6">
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <div className="absolute inset-0 border-4 border-emerald-500/20 rounded-full border-t-emerald-500 animate-spin" />
                  <Cuboid size={40} className="text-emerald-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                </div>
                <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden mb-2">
                   <div className="h-full bg-emerald-500 animate-[pulse_2s_ease-in-out_infinite] w-2/3" />
                </div>
                <p className="text-emerald-400 text-xs font-mono uppercase tracking-widest animate-pulse">Interpolando Geometría a través del nodo MCP...</p>
              </div>
            )}

            {status === 'done' && (
              <div className="absolute inset-0 p-6 flex flex-col animate-in fade-in zoom-in duration-500 overflow-y-auto">
                 <div className="flex items-center gap-3 mb-4 shrink-0">
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center border border-emerald-500/30">
                       <CheckCircle2 size={16} className="text-emerald-400" />
                    </div>
                    <span className="text-lg font-bold text-white">Análisis Terminado</span>
                 </div>
                 
                 <div className="flex-1 bg-white/5 border border-white/10 rounded-xl p-6 overflow-y-auto text-sm text-white/80 font-mono whitespace-pre-wrap leading-relaxed shadow-inner">
                    {resultQuote}
                 </div>
              </div>
            )}
          </div>

          {/* Footer del renderizador */}
          <div className="h-14 border-t border-white/5 flex items-center justify-between px-6 bg-black/50 backdrop-blur-md">
            <span className="text-[10px] uppercase font-mono text-white/30 tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> MCP Link Activo
            </span>
            {status === 'done' && (
              <button 
                onClick={handleCreateOfficial}
                className="flex items-center gap-2 text-xs font-semibold px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 hover:text-white hover:bg-emerald-500 transition-colors"
                title="Convertir esta estimación IA en un Presupuesto oficial."
              >
                <PlusCircle size={14} /> Crear Presupuesto Oficial
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};
